// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getRuntimeEnv } from "../config/runtimeEnv";
import {
  activatePaypalSubscription,
  downgradePaypalSubscriptionToFree,
  getMonetizationMe,
  getPlans,
  selectFreePlan,
} from "../api/monetization";
import { getUser, setUser } from "../auth/auth";
import { useToast } from "../components/ToastProvider";
import "../styles/Settings.css";
import "../styles/OnboardingPlan.css";

declare global {
  interface Window {
    paypal?: any;
  }
}

const PAYPAL_CLIENT_ID = getRuntimeEnv("REACT_APP_PAYPAL_CLIENT_ID");
const PAYPAL_PLAN_ID_PRO = getRuntimeEnv("REACT_APP_PAYPAL_PLAN_ID_PRO");
const PAYPAL_PLAN_ID_ELITE = getRuntimeEnv("REACT_APP_PAYPAL_PLAN_ID_ELITE");
const PAYPAL_BRAND_NAME = "Cardex";

export default function OnboardingPlan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalError, setPaypalError] = useState("");
  const [activePaypalPlan, setActivePaypalPlan] = useState<"pro" | "elite" | null>(null);
  const [currentPlanCode, setCurrentPlanCode] = useState<string>("free");
  const [downgradeModalOpen, setDowngradeModalOpen] = useState(false);
  const [pendingDowngradePlan, setPendingDowngradePlan] = useState<any | null>(null);
  const [monetization, setMonetization] = useState<any | null>(null);
  const paypalRefs = useRef<Record<"pro" | "elite", HTMLDivElement | null>>({
    pro: null,
    elite: null,
  });
  const isManageMode = useMemo(
    () => new URLSearchParams(location.search).get("mode") === "manage",
    [location.search],
  );
  const localUser = getUser();

  const formatLifecycleDate = (value?: string | null) => {
    if (!value) return "your current cycle end";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "your current cycle end";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([getPlans(), getMonetizationMe().catch(() => null)])
      .then(([plansData, monetization]) => {
        if (!mounted) return;
        setPlans(plansData || []);
        setMonetization(monetization || null);
        const resolvedPlanCode =
          String(monetization?.activePlanCode || localUser?.activePlanCode || "free").toLowerCase();
        setCurrentPlanCode(resolvedPlanCode);
      })
      .catch((error) => {
        showToast(error?.message || "Failed to load plans", "error");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [showToast, localUser?.activePlanCode]);

  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) return;

    const scriptId = "paypal-sdk-script";
    const requiredSrc = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(PAYPAL_CLIENT_ID)}&vault=true&intent=subscription&disable-funding=${encodeURIComponent("card,credit,paylater")}`;
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    const hasMatchingScript = Boolean(existing && existing.src === requiredSrc);
    const hasSubscriptionSdk = hasMatchingScript && typeof window.paypal?.Buttons === "function";

    if (hasSubscriptionSdk) {
      setPaypalReady(true);
      setPaypalError("");
      return;
    }

    if (existing && !hasMatchingScript) {
      existing.parentElement?.removeChild(existing);
    }

    if (!hasMatchingScript) {
      const otherPaypalScripts = Array.from(document.querySelectorAll('script[src*="paypal.com/sdk/js"]'));
      otherPaypalScripts.forEach((node) => {
        if (node.id !== scriptId) {
          node.parentElement?.removeChild(node);
        }
      });

      try {
        delete window.paypal;
      } catch {
        window.paypal = undefined;
      }
    }

    if (existing && hasMatchingScript) {
      if (window.paypal) {
        setPaypalReady(true);
        setPaypalError("");
        return;
      }

      const handleLoad = () => {
        setPaypalReady(true);
        setPaypalError("");
      };
      const handleError = () => {
        setPaypalReady(false);
        setPaypalError("Could not load PayPal.");
        showToast("Could not load PayPal SDK.", "error");
      };

      existing.addEventListener("load", handleLoad);
      existing.addEventListener("error", handleError);

      return () => {
        existing.removeEventListener("load", handleLoad);
        existing.removeEventListener("error", handleError);
      };
    }

    if (window.paypal) {
      setPaypalReady(true);
      setPaypalError("");
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = requiredSrc;
    script.async = true;
    script.onload = () => {
      setPaypalReady(true);
      setPaypalError("");
    };
    script.onerror = () => {
      setPaypalReady(false);
      setPaypalError("Could not load PayPal.");
      showToast("Could not load PayPal SDK.", "error");
    };
    document.body.appendChild(script);
  }, [showToast]);

  useEffect(() => {
    if (!paypalReady || !window.paypal) return;

    const planIds: Record<"pro" | "elite", string> = {
      pro: PAYPAL_PLAN_ID_PRO,
      elite: PAYPAL_PLAN_ID_ELITE,
    };

    (["pro", "elite"] as const).forEach((planCode) => {
      const container = paypalRefs.current[planCode];
      if (!container) return;

      container.innerHTML = "";

      const planId = planIds[planCode];
      const isCurrent = isManageMode && currentPlanCode === planCode;
      if (!planId || isCurrent) return;

      const buttons = window.paypal.Buttons({
          fundingSource: window.paypal?.FUNDING?.PAYPAL,
          style: {
            layout: "horizontal",
            shape: "rect",
            height: 48,
            label: "subscribe",
          },
          onClick: () => {
            setActivePaypalPlan(planCode);
            setSubmitting(true);
          },
          createSubscription: (_data: any, actions: any) =>
            actions.subscription.create({
              plan_id: planId,
              application_context: {
                brand_name: PAYPAL_BRAND_NAME,
                shipping_preference: "NO_SHIPPING",
                user_action: "SUBSCRIBE_NOW",
              },
            }),
          onApprove: async (data: any) => {
            const subscriptionId = String(data?.subscriptionID || "").trim();
            if (!subscriptionId) {
              setSubmitting(false);
              setActivePaypalPlan(null);
              showToast("Missing subscription id from PayPal.", "error");
              return;
            }
            try {
              const res = await activatePaypalSubscription({ subscriptionId, planCode });
              const localUser = getUser() || {};
              setUser({
                ...localUser,
                activePlanCode: res?.activePlanCode || planCode,
                planSelectionCompleted: true,
              });
              setCurrentPlanCode(res?.activePlanCode || planCode);
              showToast("Subscription activated.", "success");
              navigate(isManageMode ? "/profile?tab=plan" : "/home", { replace: true });
            } catch (error: any) {
              showToast(error?.message || "Failed to activate subscription.", "error");
            } finally {
              setSubmitting(false);
              setActivePaypalPlan(null);
            }
          },
          onCancel: () => {
            setSubmitting(false);
            setActivePaypalPlan(null);
          },
          onError: () => {
            setSubmitting(false);
            setActivePaypalPlan(null);
            showToast("PayPal failed to initialize. Please try again.", "error");
          },
        });

      if (!buttons?.isEligible?.() && typeof buttons?.isEligible === "function") {
        container.innerHTML = "";
        setPaypalError("PayPal is not eligible for this browser session.");
        return;
      }

      buttons
        .render(container)
        .catch(() => {
          container.innerHTML = "";
          setPaypalError("Could not render the PayPal button.");
        });
    });
  }, [paypalReady, currentPlanCode, isManageMode, navigate, showToast, loading, plans.length]);

  const sortedPlans = useMemo(() => {
    return [...plans].sort((a, b) => Number(a.monthlyPriceCents || 0) - Number(b.monthlyPriceCents || 0));
  }, [plans]);

  const handleSelectFree = async () => {
    setSubmitting(true);
    try {
      await selectFreePlan();
      const localUser = getUser() || {};
      setUser({ ...localUser, activePlanCode: "free", planSelectionCompleted: true });
      setCurrentPlanCode("free");
      showToast("Free plan selected.", "success");
      navigate(isManageMode ? "/profile?tab=plan" : "/home", { replace: true });
    } catch (error: any) {
      showToast(error?.message || "Could not select free plan.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const shouldUsePaypalForPlan = (code: string) => {
    if (!PAYPAL_CLIENT_ID) return false;
    if (code === "pro") return Boolean(PAYPAL_PLAN_ID_PRO);
    if (code === "elite") return Boolean(PAYPAL_PLAN_ID_ELITE);
    return false;
  };

  const handlePlanClick = async (plan: any) => {
    const code = String(plan?.code || "").toLowerCase();
    const isPaid = code === "pro" || code === "elite";
    const isCurrent = code === currentPlanCode;
    const isDowngradeScheduled = Boolean(monetization?.subscription?.cancelAtPeriodEnd);
    if (isManageMode && isCurrent) return;

    if (!isPaid) {
      if (isManageMode && (currentPlanCode === "pro" || currentPlanCode === "elite")) {
        if (isDowngradeScheduled) {
          showToast(
            `Downgrade already scheduled for ${formatLifecycleDate(monetization?.subscription?.downgradeEffectiveAt)}.`,
            "info",
          );
          return;
        }
        setPendingDowngradePlan(plan);
        setDowngradeModalOpen(true);
        return;
      }
      await handleSelectFree();
      return;
    }

    showToast("PayPal plan id is missing for this plan.", "error");
  };

  const confirmDowngrade = async () => {
    if (!pendingDowngradePlan) return;
    setSubmitting(true);
    try {
      const result = await downgradePaypalSubscriptionToFree();
      const currentUser = getUser() || {};
      setUser({
        ...currentUser,
        activePlanCode: result?.activePlanCode || currentPlanCode,
        planSelectionCompleted: true,
      });
      const refreshedMonetization = await getMonetizationMe().catch(() => null);
      setMonetization(refreshedMonetization || null);
      if (refreshedMonetization?.activePlanCode) {
        setCurrentPlanCode(String(refreshedMonetization.activePlanCode).toLowerCase());
      }
      showToast(result?.message || "Downgrade scheduled.", "success");
    } catch (error: any) {
      showToast(error?.message || "Could not schedule downgrade.", "error");
    } finally {
      setSubmitting(false);
      setDowngradeModalOpen(false);
      setPendingDowngradePlan(null);
    }
  };

  return (
    <main className="settings-page onboarding-plan-page">
      <section className="onboarding-plan-shell">
        <header className="settings-header onboarding-plan-header">
          <h1>{isManageMode ? "Manage Your Plan" : "Choose Your Plan"}</h1>
          <p>
            {isManageMode
              ? "Update your subscription and plan features."
              : "Pick Free, Pro, or Elite to continue."}
          </p>
        </header>

        {loading ? (
          <div className="settings-section onboarding-loading">Loading plans...</div>
        ) : (
          <div className="settings-section">
            {isManageMode && monetization?.subscription?.cancelAtPeriodEnd && (
              <div className="onboarding-paypal-card" style={{ marginBottom: 20 }}>
                <h3>Downgrade Scheduled</h3>
                <p>
                  Your {monetization?.plan?.name || "paid"} benefits stay active until{" "}
                  {formatLifecycleDate(monetization?.subscription?.downgradeEffectiveAt)}.
                </p>
              </div>
            )}
            <div className="onboarding-plan-grid">
              {sortedPlans.map((plan) => {
                const code = String(plan.code || "free");
                const price = Number(plan.monthlyPriceCents || 0) / 100;
                const isPaid = code === "pro" || code === "elite";
                const isRecommended = code === "pro";
                const isCurrent = isManageMode && code === currentPlanCode;
                const isScheduledFreeDowngrade =
                  isManageMode &&
                  code === "free" &&
                  (currentPlanCode === "pro" || currentPlanCode === "elite") &&
                  Boolean(monetization?.subscription?.cancelAtPeriodEnd);

                return (
                  <article
                    key={code}
                    className={`onboarding-plan-card ${isRecommended ? "recommended" : ""} ${isCurrent ? "current" : ""}`}
                  >
                    {isRecommended && <span className="onboarding-plan-badge">Most Popular</span>}
                    {isCurrent && <span className="onboarding-plan-current-badge">Current Plan</span>}
                    <header className="onboarding-plan-card-header">
                      <h3>{plan.name}</h3>
                      <div className="onboarding-plan-price">${price.toFixed(2)}<span>/mo</span></div>
                    </header>

                    <div className="onboarding-plan-divider" />

                    <div className="onboarding-plan-features">
                      <div>Active bounties: <strong>{plan.maxActiveBounties}</strong></div>
                      <div>Active trades: <strong>{plan.maxActiveTrades}</strong></div>
                      <div>Free boosts/month: <strong>{plan.monthlyFreeBoosts}</strong></div>
                      <div>Paid boost: <strong>${(Number(plan.paidBoostPriceCents || 0) / 100).toFixed(2)}</strong></div>
                      <div>Post lifetime: <strong>{plan.postLifetimeDays} days</strong></div>
                      <div>Boost extension: <strong>{plan.boostExtensionDays ?? 7} days</strong></div>
                      <div>Themes: <strong>{plan.themesEnabled ? "Included" : "Not included"}</strong></div>
                      <div>
                        Badges:{" "}
                        <strong>
                          {plan.badgeEliteEligible ? "Pro + Elite" : plan.badgeProEligible ? "Pro Trader" : "None"}
                        </strong>
                      </div>
                    </div>

                    <footer className="onboarding-plan-card-footer">
                      {shouldUsePaypalForPlan(code) && !isCurrent ? (
                        <div className="onboarding-paypal-inline">
                          <div
                            ref={(node) => {
                              if (code === "pro" || code === "elite") {
                                paypalRefs.current[code] = node;
                              }
                            }}
                            className={`onboarding-paypal-inline-button ${activePaypalPlan === code ? "is-busy" : ""}`}
                          />
                          <div className="onboarding-paypal-inline-note">
                            Complete your subscription securely with PayPal. Shipping is not requested for this digital plan.
                          </div>
                          {!PAYPAL_CLIENT_ID && (
                            <div className="onboarding-paypal-inline-status">PayPal client id is missing.</div>
                          )}
                          {PAYPAL_CLIENT_ID && !paypalReady && !paypalError && (
                            <div className="onboarding-paypal-inline-status">Loading PayPal...</div>
                          )}
                          {paypalError && <div className="onboarding-paypal-inline-status">{paypalError}</div>}
                        </div>
                      ) : (
                        <button
                          className={`onboarding-plan-btn ${isCurrent || isScheduledFreeDowngrade ? "secondary" : "primary"}`}
                          onClick={() => void handlePlanClick(plan)}
                          disabled={submitting || isCurrent || isScheduledFreeDowngrade}
                        >
                          {isCurrent
                            ? "Current Plan"
                            : isScheduledFreeDowngrade
                              ? "Downgrade Scheduled"
                              : !isPaid
                                ? "Select Free"
                                : `Continue with ${plan.name}`}
                        </button>
                      )}
                    </footer>
                  </article>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {downgradeModalOpen && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content">
            <h2>Confirm Downgrade</h2>
            <p>
              Your paid subscription will be cancelled at period end. You will keep your current benefits until{" "}
              {formatLifecycleDate(monetization?.subscription?.downgradeEffectiveAt)} and then move to Free automatically.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn cancel"
                onClick={() => {
                  setDowngradeModalOpen(false);
                  setPendingDowngradePlan(null);
                }}
              >
                Cancel
              </button>
              <button type="button" className="modal-btn confirm" onClick={() => void confirmDowngrade()}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
